import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { apiGet, apiPost, apiPut } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CATEGORIES, CONDITIONS } from "../lib/categories";
import { PhotoUploader } from "../components/PhotoUploader";
import { SizeField } from "../components/SizeField";
import { Alert, Button, Field, Input, Select, Textarea, Spinner, EmptyState } from "../components/ui";

export function EditListingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState(null);
  // Mixes existing https URLs with newly added data: URLs — PhotoUploader
  // renders both, and submit only uploads the new ones.
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("");

  useEffect(() => {
    let cancelled = false;
    apiGet(`/listings/${id}`)
      .then(({ listing }) => {
        if (cancelled) return;
        setListing(listing);
        setForm({
          title: listing.title || "",
          category: listing.category || "",
          condition: listing.condition || "good",
          size: listing.size || "",
          price: String(listing.price ?? ""),
          description: listing.description || "",
          status: listing.status || "active",
        });
        // Rehydrate into the { full, thumb } pairs the uploader works with.
        // Listings from before thumbnails have none, so thumb is null and the
        // uploader falls back to the full image.
        setPhotos(
          (listing.photos || []).map((url, i) => ({
            full: url,
            thumb: (listing.thumbs || [])[i] || null,
          }))
        );
      })
      .catch((e) => !cancelled && setLoadError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }
  if (loadError || !listing) {
    return (
      <EmptyState title="Listing not found">
        {loadError || "This item may have been removed."}{" "}
        <Link to="/gear" className="text-brand-600 underline">
          Back to browse
        </Link>
      </EmptyState>
    );
  }

  const isOwner = user && listing.sellerId === user.userId;
  const canEdit = isOwner || (user && user.isAdmin);
  if (!canEdit) {
    return (
      <EmptyState title="You can't edit this listing">
        Only the seller can change it.{" "}
        <Link to={`/gear/listing/${id}`} className="text-brand-600 underline">
          Back to the listing
        </Link>
      </EmptyState>
    );
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!form.title.trim()) return setError("Please add a title.");
    if (!form.category) return setError("Please choose a category.");
    const price = Number(form.price);
    if (!Number.isFinite(price) || price < 0) return setError("Enter a valid price.");

    setBusy(true);
    try {
      // Only newly added items need uploading — re-saving must not re-upload
      // what's already hosted.
      const isNew = (p) => typeof p.full === "string" && p.full.startsWith("data:");
      const fresh = photos.filter(isNew);
      let uploaded = [];
      let uploadedThumbs = [];
      if (fresh.length) {
        setStep("Uploading photos…");
        const { urls, thumbUrls } = await apiPost("/photo-upload", { images: fresh });
        uploaded = urls || [];
        uploadedThumbs = thumbUrls || [];
      }
      setStep("Saving…");
      // Rebuild IN THE UPLOADER'S ORDER — the first photo is the cover, so
      // appending new ones would silently ignore a reorder. Photos and thumbs
      // stay index-aligned because they were carried as pairs throughout.
      const resolved = photos.map((p) => {
        if (!isNew(p)) return { full: p.full, thumb: p.thumb || null };
        const i = fresh.indexOf(p);
        return { full: uploaded[i], thumb: uploadedThumbs[i] || null };
      });
      const finalPhotos = resolved.map((p) => p.full).filter(Boolean);
      const finalThumbs = resolved.filter((p) => p.full).map((p) => p.thumb);

      await apiPut(`/listings/${id}`, {
        title: form.title.trim(),
        category: form.category,
        condition: form.condition,
        size: form.size.trim(),
        price,
        description: form.description.trim(),
        status: form.status,
        photos: finalPhotos,
        thumbs: finalThumbs,
      });
      navigate(`/gear/listing/${id}`);
    } catch (err) {
      setError(err.message || "Couldn't save your changes.");
    } finally {
      setBusy(false);
      setStep("");
    }
  }

  return (
    <div className="max-w-xl mx-auto">
      <button
        onClick={() => navigate(`/gear/listing/${id}`)}
        className="text-sm text-slate-500 hover:text-slate-700 mb-3"
      >
        ← Back to listing
      </button>
      <h1 className="text-xl font-bold text-slate-800">Edit listing</h1>
      {!isOwner && (
        <p className="mt-1 text-sm text-amber-700">
          You're editing another member's listing as an admin.
        </p>
      )}

      <form onSubmit={submit} className="mt-4 space-y-4">
        <Field label="Photos">
          <PhotoUploader value={photos} onChange={setPhotos} />
        </Field>
        <Field label="Title">
          <Input value={form.title} onChange={set("title")} maxLength={100} required />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <Select value={form.category} onChange={set("category")} required>
              <option value="">Choose…</option>
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Condition">
            <Select value={form.condition} onChange={set("condition")}>
              {CONDITIONS.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Price">
            <Input type="number" min="0" step="1" value={form.price} onChange={set("price")} required />
          </Field>
          <SizeField
            category={form.category}
            value={form.size}
            onChange={(size) => setForm((f) => ({ ...f, size }))}
          />
        </div>
        <Field label="Status">
          <Select value={form.status} onChange={set("status")}>
            <option value="active">Available</option>
            <option value="sold">Sold</option>
          </Select>
        </Field>
        <Field label="Description">
          <Textarea rows={4} value={form.description} onChange={set("description")} />
        </Field>

        <Alert>{error}</Alert>
        <div className="flex gap-2">
          <Button type="submit" disabled={busy} className="flex-1">
            {busy ? step || <Spinner /> : "Save changes"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(`/gear/listing/${id}`)}
            disabled={busy}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
